import { useState, useCallback, useRef } from "react";
import { useLocation } from "wouter";
import { useBooking } from "@/hooks/use-booking-context";
import { useToast } from "@/hooks/use-toast";
import { calculateNights, formatDateInput } from "@/lib/date-utils";
import { apiRequest } from "@/lib/queryClient";
import { getPromoToken } from "@/lib/promo";
import { trackEvent, newEventId } from "@/lib/meta-pixel";
import type { PricingBreakdown } from "@/lib/pricing-utils";
import { QuoteError, requestQuote } from "@/lib/quote-request";

interface BookingDataForCheckout {
  checkIn: string;
  checkOut: string;
  guests: number;
  nights: number;
  total: string;
  nightlyRate: string;
  breakdown: PricingBreakdown['breakdown'];
  securityDeposit?: string | null;
  couponCode?: string | null;
  paymentPlan?: 'full' | 'split';
  propertyId?: string;
  propertySlug?: string;
}

interface UseDirectCheckoutReturn {
  isLoading: boolean;
  isGuestModalOpen: boolean;
  bookingData: BookingDataForCheckout | null;
  /**
   * `propertyTitle` is passed only so the Meta InitiateCheckout event can name
   * the home. Without it the event reports a generic title for both houses and
   * the two properties cannot be told apart in Meta's funnel reporting.
   */
  beginCheckout: (couponCode?: string, paymentPlan?: 'full' | 'split', propertyTitle?: string) => Promise<void>;
  closeGuestModal: () => void;
}

export function useDirectCheckout(selectedProperty?: { propertyId?: string; propertySlug?: string; onQuoteError?: (error: Error) => void }): UseDirectCheckoutReturn {
  const [, setLocation] = useLocation();
  const { checkIn, checkOut, guests, isValidBooking, getDateValidationErrors, propertyId: contextPropertyId, propertySlug: contextPropertySlug } = useBooking();
  const propertyId = selectedProperty?.propertyId || contextPropertyId;
  const propertySlug = selectedProperty?.propertySlug || (selectedProperty?.propertyId ? undefined : contextPropertySlug);
  const currentSelection = `${propertyId}|${propertySlug}|${checkIn}|${checkOut}|${guests}`;
  const selectionRef = useRef(currentSelection);
  selectionRef.current = currentSelection;
  const submittingRef = useRef(false);
  const { toast } = useToast();
  
  const [isLoading, setIsLoading] = useState(false);
  const [isGuestModalOpen, setIsGuestModalOpen] = useState(false);
  const [bookingData, setBookingData] = useState<BookingDataForCheckout | null>(null);

  const beginCheckout = useCallback(async (couponCode?: string, paymentPlan: 'full' | 'split' = 'full', propertyTitle?: string) => {
    if (submittingRef.current) return;
    if (!checkIn || !checkOut) {
      toast({
        title: "Select dates",
        description: "Please select your check-in and check-out dates.",
        variant: "destructive",
      });
      return;
    }

    if (!isValidBooking()) {
      const errors = getDateValidationErrors();
      toast({
        title: "Invalid dates",
        description: errors[0] || "Please select valid booking dates.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    submittingRef.current = true;

    try {
      const guestCount = parseInt(guests) || 2;
      const nights = calculateNights(checkIn, checkOut);

      const formattedCheckIn = formatDateInput(checkIn);
      const formattedCheckOut = formatDateInput(checkOut);
      const params = new URLSearchParams({
        start: formattedCheckIn || '',
        end: formattedCheckOut || '',
        guests: guestCount.toString()
      });
      if (couponCode) {
        params.set('coupon', couponCode);
      }
      // Include the urgency token so a limited-time 10% quote matches what the server
      // will honor at booking time.
      const promoToken = getPromoToken();
      if (promoToken) {
        params.set('promo_token', promoToken);
      }
      if (propertySlug) params.set('property', propertySlug);
      else if (propertyId) params.set('propertyId', propertyId);

      const pricing = await requestQuote(params);
      if (selectionRef.current !== currentSelection) throw new Error("Your home or dates changed. Please review the updated price before reserving.");

      if (!pricing || pricing.total === undefined) {
        throw new Error("Unable to calculate pricing");
      }

      const nightlyRateItem = pricing.breakdown?.find(item => 
        item.label.toLowerCase().includes('night')
      );

      const bookingDataForModal: BookingDataForCheckout = {
        checkIn: formattedCheckIn || checkIn,
        checkOut: formattedCheckOut || checkOut,
        guests: guestCount,
        nights,
        total: pricing.total.toString(),
        nightlyRate: nightlyRateItem?.amount?.toString() || "0",
        breakdown: pricing.breakdown,
        securityDeposit: pricing.securityDeposit || null,
        couponCode: couponCode || pricing.coupon?.code || null,
        paymentPlan,
        propertyId,
        propertySlug,
      };

      setBookingData(bookingDataForModal);

      // Meta conversion signal: the guest has a real quote and is entering checkout.
      // Carries an event id so a duplicate fire for the same click is counted once.
      trackEvent(
        "InitiateCheckout",
        {
          value: parseFloat(pricing.total) || 0,
          currency: "USD",
          // Name and id the actual home. This used to be a hardcoded generic
          // string with no content id, so every checkout looked identical to
          // Meta and neither house could be credited for the ones it produced.
          content_name: propertyTitle || "Oahu beachfront vacation rental",
          content_type: "product",
          ...(propertySlug ? { content_ids: [propertySlug] } : {}),
          num_items: nights,
        },
        newEventId("ic"),
      );

      const existingGuestInfo = sessionStorage.getItem("guestInfo");
      
      if (existingGuestInfo) {
        try {
          const guestInfo = JSON.parse(existingGuestInfo);
          if (guestInfo.firstName && guestInfo.email && guestInfo.phone) {
            sessionStorage.setItem("pendingBooking", JSON.stringify({
              bookingId: `booking-${Date.now()}`,
              checkIn: bookingDataForModal.checkIn,
              checkOut: bookingDataForModal.checkOut,
              guests: guestCount,
              total: pricing.total.toString(),
              currency: "usd",
              nights,
              nightlyRate: bookingDataForModal.nightlyRate,
              breakdown: pricing.breakdown,
              securityDeposit: pricing.securityDeposit || null,
              couponCode: couponCode || null,
              paymentPlan,
              propertyId,
              propertySlug,
            }));

            setLocation("/booking/checkout");
            return;
          }
        } catch (e) {
          sessionStorage.removeItem("guestInfo");
        }
      }

      setIsGuestModalOpen(true);
    } catch (error: any) {
      if (selectionRef.current === currentSelection) selectedProperty?.onQuoteError?.(error instanceof Error ? error : new Error("Unable to calculate pricing."));
      toast({
        title: error instanceof QuoteError && error.needsDateChange ? "Change dates" : "Error",
        description: error.message || "Failed to start checkout. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      submittingRef.current = false;
    }
  }, [checkIn, checkOut, guests, isValidBooking, getDateValidationErrors, toast, setLocation, propertyId, propertySlug, currentSelection, selectedProperty?.onQuoteError]);

  const closeGuestModal = useCallback(() => {
    setIsGuestModalOpen(false);
  }, []);

  return {
    isLoading,
    isGuestModalOpen,
    bookingData,
    beginCheckout,
    closeGuestModal,
  };
}
